import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, MenuItem, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, type PropertyScan } from '../services/api';
import {
  SURFACE_TYPE_OPTIONS, COATING_OPTIONS, CONDITION_OPTIONS, EXPOSURE_OPTIONS,
  labelize,
} from '../constants/propertyScan';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#F5F7FA', bgcolor: '#1A1D21', borderRadius: '10px',
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: '1px solid #00AAFF' },
  },
};

type EditableField = 'surface_type' | 'existing_coating' | 'current_condition' | 'color_sheen'
  | 'coats_detected' | 'paint_thickness_mils' | 'exposure' | 'square_footage' | 'location';

const TILES: Array<{
  key: EditableField; label: string; type: 'select' | 'text' | 'number';
  options?: { value: string; label: string }[];
}> = [
  { key: 'surface_type', label: 'Surface Type', type: 'select', options: SURFACE_TYPE_OPTIONS },
  { key: 'existing_coating', label: 'Existing Coating', type: 'select', options: COATING_OPTIONS },
  { key: 'current_condition', label: 'Current Condition', type: 'select', options: CONDITION_OPTIONS },
  { key: 'color_sheen', label: 'Color / Sheen', type: 'text' },
  { key: 'coats_detected', label: 'Coats Detected', type: 'number' },
  { key: 'paint_thickness_mils', label: 'Paint Thickness', type: 'number' },
  { key: 'exposure', label: 'Exposure', type: 'select', options: EXPOSURE_OPTIONS },
  { key: 'square_footage', label: 'Square Footage', type: 'number' },
];

function formatValue(scan: PropertyScan, key: EditableField): string {
  const v = (scan as any)[key];
  if (v === null || v === undefined || v === '') return '—';
  if (key === 'paint_thickness_mils') return `${v} mils`;
  if (key === 'square_footage') return Number(v).toLocaleString();
  if (key === 'color_sheen' || key === 'location') return String(v);
  return labelize(String(v));
}

function formatMoney(v: number): string {
  return v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${Math.round(v)}`;
}

function TileIcon() {
  return (
    <Box sx={{
      width: 32, height: 32, borderRadius: 1, bgcolor: '#1A1D21', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5,
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2C7FB0' }} />
    </Box>
  );
}

export default function PropertyScanReportPage() {
  const { scanId } = useParams<{ scanId: string }>();
  const [scan, setScan] = useState<PropertyScan | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!scanId) return;
    api.getPropertyScan(scanId).then(setScan).finally(() => setLoading(false));
  }, [scanId]);

  const startEdit = () => {
    if (!scan) return;
    setForm({
      surface_type: scan.surface_type ?? '',
      existing_coating: scan.existing_coating ?? '',
      current_condition: scan.current_condition ?? '',
      color_sheen: scan.color_sheen ?? '',
      coats_detected: scan.coats_detected != null ? String(scan.coats_detected) : '',
      paint_thickness_mils: scan.paint_thickness_mils != null ? String(scan.paint_thickness_mils) : '',
      exposure: scan.exposure ?? '',
      square_footage: scan.square_footage != null ? String(scan.square_footage) : '',
      location: scan.location ?? '',
    });
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveEdit = async () => {
    if (!scanId) return;
    setSaving(true);
    try {
      const updated = await api.updatePropertyScan(scanId, {
        surface_type: form.surface_type || undefined,
        existing_coating: form.existing_coating || undefined,
        current_condition: (form.current_condition || undefined) as any,
        color_sheen: form.color_sheen || undefined,
        coats_detected: form.coats_detected ? Number(form.coats_detected) : undefined,
        paint_thickness_mils: form.paint_thickness_mils ? Number(form.paint_thickness_mils) : undefined,
        exposure: (form.exposure || undefined) as any,
        square_footage: form.square_footage ? Number(form.square_footage) : undefined,
        location: form.location || undefined,
      });
      setScan(updated);
      setEditing(false);
      toast.success('Scan updated');
    } catch {
      toast.error('Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: '#00AAFF' }} /></Box>;
  if (!scan) return <Box sx={{ p: 3 }}><Typography sx={{ color: '#8A8F98' }}>Scan not found.</Typography></Box>;

  const score = scan.condition_score;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#F5F7FA', fontWeight: 'bold' }}>{scan.property_name}</Typography>
          {scan.location && <Typography sx={{ color: '#8A8F98', fontSize: 13 }}>📍 {scan.location}</Typography>}
        </Box>
        {!editing ? (
          <Button startIcon={<EditIcon />} onClick={startEdit}
            sx={{ borderColor: 'rgba(255,255,255,0.15)', color: '#8A8F98', borderRadius: 999, textTransform: 'none' }} variant="outlined">Edit</Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<CloseIcon />} onClick={cancelEdit} disabled={saving} sx={{ color: '#8A8F98', textTransform: 'none' }}>Cancel</Button>
            <Button startIcon={<CheckIcon />} onClick={saveEdit} disabled={saving} variant="contained"
              sx={{ bgcolor: '#00AAFF', borderRadius: 999, textTransform: 'none', '&:hover': { bgcolor: '#0090DD' } }}>
              {saving ? <CircularProgress size={18} /> : 'Save'}
            </Button>
          </Box>
        )}
      </Box>

      {/* 1. Property Scan & Data Capture */}
      <Typography sx={{ color: '#00AAFF', fontWeight: 700, fontSize: 13, letterSpacing: 0.5, mb: 1.5 }}>
        1. PROPERTY SCAN &amp; DATA CAPTURE
      </Typography>
      <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {TILES.map((tile) => (
              <Grid item xs={editing ? 12 : 6} key={tile.key}>
                {editing ? (
                  <>
                    <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      {tile.label}
                    </Typography>
                    {tile.type === 'select' ? (
                      <TextField select fullWidth size="small" value={form[tile.key]}
                        onChange={(e) => setForm((f) => ({ ...f, [tile.key]: e.target.value }))} sx={fieldSx}
                        SelectProps={{ displayEmpty: true }}>
                        <MenuItem value="">Select…</MenuItem>
                        {tile.options!.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </TextField>
                    ) : (
                      <TextField fullWidth size="small" type={tile.type === 'number' ? 'number' : 'text'}
                        value={form[tile.key]} onChange={(e) => setForm((f) => ({ ...f, [tile.key]: e.target.value }))}
                        sx={fieldSx} />
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TileIcon />
                    <Box>
                      <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
                        {tile.label}
                      </Typography>
                      <Typography sx={{ color: '#F5F7FA', fontWeight: 600, fontSize: 15 }}>
                        {formatValue(scan, tile.key)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            ))}
            {editing && (
              <Grid item xs={12}>
                <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                  Location
                </Typography>
                <TextField fullWidth size="small" placeholder="e.g. Tacoma, WA"
                  value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  sx={fieldSx} />
              </Grid>
            )}
          </Grid>

          {scan.image_url && (
            <Box sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden' }}>
              <Box component="img" src={scan.image_url} alt={scan.property_name}
                sx={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />
              {score != null && (
                <Box sx={{
                  position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(10,10,12,0.85)',
                  borderRadius: 1.5, px: 1.5, py: 0.75, textAlign: 'center',
                }}>
                  <Typography sx={{ color: '#8A8F98', fontSize: 10, letterSpacing: 1 }}>SCORE</Typography>
                  <Typography sx={{ color: '#F5F7FA', fontWeight: 'bold', fontSize: 22, lineHeight: 1 }}>
                    {score}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 2. AI-Powered Insights */}
      <Typography sx={{ color: '#00AAFF', fontWeight: 700, fontSize: 13, letterSpacing: 0.5, mb: 1.5 }}>
        2. AI-POWERED INSIGHTS
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {scan.material_estimate && (
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  Material Estimate
                </Typography>
                {[
                  ['Primer', `${scan.material_estimate.primer_gallons} gal`],
                  ['Topcoat', `${scan.material_estimate.topcoat_gallons} gal`],
                  ['Rec. Coats', String(scan.material_estimate.recommended_coats)],
                  ['Waste Factor', `${scan.material_estimate.waste_factor_pct}%`],
                ].map(([label, val]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ color: '#8A8F98', fontSize: 13 }}>{label}</Typography>
                    <Typography sx={{ color: '#F5F7FA', fontSize: 13, fontWeight: 600 }}>{val}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {scan.recommended_products && scan.recommended_products.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  Product Recommendation
                </Typography>
                {scan.recommended_products.map((p, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Typography sx={{ color: '#F5F7FA', fontWeight: 600, fontSize: 13 }}>{p.name}</Typography>
                    <Typography sx={{ color: '#8A8F98', fontSize: 12 }}>
                      {p.coverage_sqft_per_gal} SF/gal · {p.lifespan_years}yr lifespan
                    </Typography>
                    <Typography sx={{ color: '#6E7681', fontSize: 12 }}>{p.description}</Typography>
                  </Box>
                ))}
                <Button component={Link} to="/products" size="small" sx={{ color: '#00AAFF', px: 0, textTransform: 'none' }}>
                  View all products →
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {scan.cost_prediction && (
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                  Cost Prediction
                </Typography>
                {[
                  ['Low', scan.cost_prediction.low, '#4ADE80'],
                  ['Average', scan.cost_prediction.average, '#00AAFF'],
                  ['High', scan.cost_prediction.high, '#EF4444'],
                ].map(([label, val, color]) => (
                  <Box key={label as string} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ color: '#8A8F98', fontSize: 13 }}>{label}</Typography>
                    <Typography sx={{ color: color as string, fontSize: 13, fontWeight: 700 }}>{formatMoney(val as number)}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {scan.ai_visual_assessment && (
        <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography sx={{ color: '#8A8F98', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              AI Visual Assessment
            </Typography>
            <Typography sx={{ color: '#C7D0DA', fontSize: 14, lineHeight: 1.6 }}>{scan.ai_visual_assessment}</Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ textAlign: 'center', py: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography sx={{ color: '#F5F7FA', fontWeight: 700, fontSize: 13, letterSpacing: 0.5, mb: 2 }}>
          THE SMARTEST DATA. THE BEST DECISIONS. THE LONGEST LASTING RESULTS.
        </Typography>
        <Grid container spacing={2}>
          {['More Accurate Estimates', 'Better Performance', 'Lower Total Cost', 'Longer Lasting Properties'].map((c) => (
            <Grid item xs={6} md={3} key={c}>
              <Typography sx={{ color: '#6E7681', fontSize: 12 }}>{c}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
